<ng-template
  #stopRow
  let-stop="stop"
  let-isFirstStop="isFirstStop"
  let-isLastStop="isLastStop"
  >
  <div class="flex">
    <app-stop-indicator
      [isFirstStop]="isFirstStop"
      [isLastStop]="isLastStop"
      [class]="
        stop.stopSequence < boardingStationSeq ||
        stop.stopSequence > dropingStationSeq
          ? 'disabled'
          : null
      "
      />

      <!--
        a11y/contrast: out-of-segment stops previously used text-gray-300 (#dbdfe5)
        which yielded ~1.4:1 contrast on white (audit flagged WCAG 1.4.3 fail).
        Switched to subText (#5C748F, ~4.9:1) so the "faded" stops still read as
        secondary visually but pass AA for normal text.
      -->
      <div
        class="flex justify-between w-full items-center"
      [ngClass]="{
        '!text-subText':
          stop.stopSequence < boardingStationSeq ||
          stop.stopSequence > dropingStationSeq,
        '!text-[#D4282E] !line-through': stop.nextStopTime?.isCanceled,
        'text-[#008667]': stop.nextStopTime?.isAlternative,
      }"
        >
        <div
          class="flex flex-col justify-center !h-full p-2 self-center text-current cursor-pointer"
          [ngClass]="{ 'pt-0': !!isFirstStop, 'pb-0': !!isLastStop }"
          (click)="pickStop(stop.stopcode)"
          >
          <div class="stop-name text-current !text-base">{{ getStopName(stop) }}</div>
          <div class="flex gap-2 items-center text-current">
            <small class="stop-code text-sm font-normal"
              >{{ stationCodeText }}: {{ stop.stopcode }}</small
              >
              @if (!!stop.isPickup && !stop.isDropoff) {
                <small class="text-sm font-normal"
                  >● {{ dropOffStationText }}</small
                  >
                }
                @if (!!stop.isDropoff && !stop.isPickup) {
                  <small class="text-sm font-normal"
                    >● {{ boardingStationText }}</small
                    >
                  }
                  @if (!!stop.isPickup && !!stop.isDropoff) {
                    <small
                      class="text-red-500 text-sm font-normal"
                      >● {{ noPickupOrDropOffText }}</small
                      >
                    }
                    @if (stop.platformNumber) {
                      <!-- contrast: #959799 (~3:1) failed AA; subText (#5C748F, ~4.9:1) passes. -->
                      <small class="text-sm">
                        {{ platformText }} {{ stop.platformNumber }}</small
                        >
                      }
                    </div>
                  </div>
                  <span
                    class="justify-self-end text-sm font-normal"
        [ngClass]="{
          '!text-[#D4282E] !line-through': stop.nextStopTime?.isCanceled,
          '!text-[#008667]':
            stop.nextStopTime?.realTimeDepartureTimeString &&
            stop.stopSequence >= boardingStationSeq &&
            stop.stopSequence <= dropingStationSeq,
        }"
                    >{{
                    (stop.nextStopTime?.realTimeDepartureTimeString ||
                    stop.nextStopTime?.departureTimeString) | hour
                    }}</span
                    >
                  </div>
                </div>
              </ng-template>

              @if (previousStops.length) {
                <!-- Screen readers only: groups grey "before boarding" stops; no visual change -->
                <h3 class="sr-only" id="search-line-previous-stops-heading">
                  {{ previousStationsText }}
                </h3>
                <!--
                  a11y: native ordered list. Explicit role="list" / role="listitem"
                  protects against Safari's known quirk where `list-style: none`
                  removes list semantics from the a11y tree. The `display: contents`
                  on each <li> keeps the existing visual layout untouched while
                  preserving the listitem role.
                -->
                <ol
                  class="list-none m-0 p-0 w-full"
                  role="list"
                  [attr.aria-labelledby]="'search-line-previous-stops-heading'">
                  @for (stop of previousStops; track stop; let i = $index) {
                    <li class="contents" role="listitem">
                      <ng-container
            *ngTemplateOutlet="
              stopRow;
              context: {
                stop: stop,
                isFirstStop: i === 0,
                isLastStop:
                  i === previousStops.length - 1 &&
                  !middleStops.length &&
                  !afterDropoffStops.length,
              }
            "
                      />
                    </li>
                  }
                </ol>
              }

              @if (middleStops.length) {
                <!-- SR-only heading mirrors the previousStops pattern so each <ol> has a discoverable name. -->
                <h3 class="sr-only" id="search-line-middle-stops-heading">
                  {{ middleStationsText }}
                </h3>
                <ol
                  class="list-none m-0 p-0 w-full"
                  role="list"
                  [attr.aria-labelledby]="'search-line-middle-stops-heading'">
                  @for (stop of middleStops; track stop; let i = $index) {
                    <li class="contents" role="listitem">
                      <ng-container
        *ngTemplateOutlet="
          stopRow;
          context: {
            stop: stop,
            isFirstStop: i === 0 && !previousStops.length,
            isLastStop: i === middleStops.length - 1 && !afterDropoffStops.length,
          }
        "
                      />
                    </li>
                  }
                </ol>
              }

              @if (afterDropoffStops.length) {
                <h3 class="sr-only" id="search-line-after-dropoff-stops-heading">
                  {{ afterDropoffStationsText }}
                </h3>
                <ol
                  class="list-none m-0 p-0 w-full"
                  role="list"
                  [attr.aria-labelledby]="'search-line-after-dropoff-stops-heading'">
                  @for (stop of afterDropoffStops; track stop; let i = $index) {
                    <li class="contents" role="listitem">
                      <ng-container
        *ngTemplateOutlet="
          stopRow;
          context: {
            stop: stop,
            isFirstStop:
              i === 0 && !previousStops.length && !middleStops.length,
            isLastStop: i === afterDropoffStops.length - 1,
          }
        "
                      />
                    </li>
                  }
                </ol>
              }
